<template>
  <section class="admin-shell">
    <header class="admin-header">
      <p class="admin-tag">后台 · 软删除 / 增删改查</p>
      <h1>数据管理控制台</h1>
      <p>仅支持通过密码认证的访问，所有操作都会操作 <code>delfile</code> 软删除标记。</p>
    </header>

    <section v-if="!isAuthenticated" class="admin-auth">
      <p class="prompt">请输入管理口令以访问记录操作接口</p>
      <input
        v-model="passwordInput"
        type="password"
        placeholder="管理口令"
        @keyup.enter="verifyPassword"
      />
      <button type="button" @click="verifyPassword">验证并加载数据</button>
      <p v-if="authError" class="inline-error">{{ authError }}</p>
    </section>

    <section v-else class="admin-body">
      <div class="admin-grid">
        <div class="entry-list">
          <div class="entry-list-header">
            <h2>现有文档</h2>
            <span>记录总数：{{ entries.length }}</span>
          </div>
          <div
            v-for="entry in entries"
            :key="entry.id"
            class="entry-row"
            :class="{ deleted: entry.delfile === 1, active: selectedEntry && selectedEntry.id === entry.id }"
          >
            <div class="entry-main" @click="selectEntry(entry)">
              <p class="entry-subject">{{ entry.subject }}</p>
              <div class="entry-meta">
                <span>FID：{{ entry.fid }} · {{ entry.fidname }}</span>
                <span>长度：{{ entry.message_length }} 字</span>
                <span>更新时间：{{ entry.uptime }}</span>
              </div>
            </div>
            <div class="entry-actions">
              <button type="button" @click="selectEntry(entry)">查看/编辑</button>
              <button
                type="button"
                v-if="entry.delfile === 1"
                @click="toggleDelete(entry, false)"
              >
                恢复
              </button>
              <button
                type="button"
                v-else
                @click="toggleDelete(entry, true)"
              >
                软删除
              </button>
            </div>
          </div>
        </div>

        <div class="entry-editor">
          <article v-if="selectedEntry">
            <header>
              <h2>编辑文档</h2>
              <small>message 长度：{{ editMessage.length }} 字符</small>
            </header>
            <div class="form-group">
              <label>文件名（subject）</label>
              <input v-model="editSubject" />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>板块 FID</label>
                <input v-model.number="editFid" type="number" min="0" />
              </div>
              <div class="form-group">
                <label>板块名称</label>
                <input v-model="editFidname" />
              </div>
            </div>
            <div class="form-group">
              <label>message 内容</label>
              <textarea v-model="editMessage" rows="12"></textarea>
            </div>
            <div class="editor-actions">
              <button type="button" @click="updateEntry" :disabled="updatePending">
                {{ updatePending ? '保存中…' : '保存修改' }}
              </button>
            </div>
            <p v-if="entryFeedback" class="inline-error">{{ entryFeedback }}</p>
          </article>
          <div v-else class="empty-state">
            请选择左侧列表中的记录即可查看 message 长度并启动编辑。
          </div>
        </div>
      </div>

      <section class="upload-card">
        <h2>上传 TXT 文本</h2>
        <p>仅支持 <code>txt</code> 文件，系统会将内容保存到 message 字段。</p>
        <div class="form-row">
          <div class="form-group">
            <label>板块 FID</label>
            <input v-model.number="uploadPayload.fid" type="number" min="0" />
          </div>
          <div class="form-group">
            <label>板块名称(fidname)</label>
            <input v-model="uploadPayload.fidname" />
          </div>
          <div class="form-group">
            <label>subject (文件名)</label>
            <input v-model="uploadPayload.subject" />
          </div>
        </div>

        <div class="form-group">
          <label>TXT 文件</label>
          <input type="file" accept=".txt" @change="handleUploadFile" />
        </div>

        <div class="form-group">
          <label>正文预览（可从文件或手动粘贴）</label>
          <textarea v-model="uploadPayload.message" rows="8"></textarea>
        </div>

        <div class="editor-actions">
          <button type="button" @click="submitUpload" :disabled="uploadPending">
            {{ uploadPending ? '上传中…' : '上传并新增记录' }}
          </button>
          <span class="hint">message 长度：{{ uploadPayload.message.length }} 字符</span>
        </div>

        <p v-if="uploadFeedback" class="inline-error">{{ uploadFeedback }}</p>
      </section>
    </section>
  </section>
</template>

<script>
const API_BASE = (process.env.VUE_APP_API_BASE || '').replace(/\/\$/, '')

function buildApiPath(path) {
  return `${API_BASE}${path}`
}

export default {
  name: 'AdminDashboard',
  data() {
    return {
      passwordInput: '',
      isAuthenticated: false,
      authError: '',
      authPassword: '',
      entries: [],
      selectedEntry: null,
      entryFeedback: '',
      editSubject: '',
      editFid: null,
      editFidname: '',
      editMessage: '',
      updatePending: false,
      uploadPayload: {
        fid: null,
        fidname: '',
        subject: '',
        message: '',
      },
      uploadPending: false,
      uploadFeedback: '',
    }
  },
  methods: {
    async verifyPassword() {
      const trimmed = this.passwordInput.trim()
      if (!trimmed) {
        this.authError = '请输入管理口令'
        return
      }
      try {
        await this.loadEntries(trimmed)
        this.isAuthenticated = true
        this.authError = ''
        this.authPassword = trimmed
        this.passwordInput = ''
      } catch (error) {
        this.authError = error.message
      }
    },
    async loadEntries(password) {
      const authPassword = password || this.authPassword
      if (!authPassword) {
        throw new Error('请先验证管理口令')
      }
      try {
        const params = new URLSearchParams()
        params.append('action', 'list')
        params.append('include_deleted', '1')
        params.append('password', authPassword)
        const response = await fetch(buildApiPath(`/api/admin.php?${params.toString()}`))
        if (!response.ok) {
          throw new Error('读取记录失败')
        }
        const payload = await response.json()
        this.entries = (payload.entries || []).sort((a, b) => {
          const timeA = new Date(a.uptime || a.created_at || 0)
          const timeB = new Date(b.uptime || b.created_at || 0)
          return timeB - timeA
        })
        this.entryFeedback = ''
      } catch (error) {
        this.entryFeedback = error.message
        throw error
      }
    },
    async selectEntry(entry) {
      this.selectedEntry = entry
      this.entryFeedback = ''
      this.editSubject = entry.subject
      this.editFid = entry.fid
      this.editFidname = entry.fidname
      this.editMessage = ''
      try {
        const params = new URLSearchParams()
        params.append('id', entry.id)
        params.append('full', '1')
        const response = await fetch(buildApiPath(`/api/content.php?${params.toString()}`))
        if (!response.ok) {
          throw new Error('加载内容失败')
        }
        const payload = await response.json()
        this.editMessage = payload.content || ''
      } catch (error) {
        this.entryFeedback = error.message
      }
    },
    async updateEntry() {
      if (!this.selectedEntry) {
        this.entryFeedback = '请先选择一条记录'
        return
      }
      const authPassword = this.authPassword
      if (!authPassword) {
        this.entryFeedback = '请先验证管理口令'
        return
      }
      this.updatePending = true
      this.entryFeedback = ''
      try {
        const response = await fetch(buildApiPath('/api/admin.php?action=update'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: this.selectedEntry.id,
            subject: this.editSubject,
            fid: this.editFid,
            fidname: this.editFidname,
            message: this.editMessage,
            password: authPassword,
          }),
        })
        if (!response.ok) {
          throw new Error('更新失败')
        }
        const payload = await response.json()
        if (!payload.success) {
          throw new Error(payload.message || '更新失败')
        }
        this.entryFeedback = '更新成功'
        await this.loadEntries()
      } catch (error) {
        this.entryFeedback = error.message
      } finally {
        this.updatePending = false
      }
    },
    async toggleDelete(entry, markAsDeleted) {
      const authPassword = this.authPassword
      if (!authPassword) {
        this.entryFeedback = '请先验证管理口令'
        return
      }
      this.entryFeedback = ''
      try {
        const action = markAsDeleted ? 'delete' : 'restore'
        const response = await fetch(buildApiPath(`/api/admin.php?action=${action}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: entry.id, password: authPassword }),
        })
        if (!response.ok) {
          throw new Error('操作失败')
        }
        const payload = await response.json()
        if (!payload.success) {
          throw new Error(payload.message || '操作失败')
        }
        this.entryFeedback = payload.message || '操作成功'
        await this.loadEntries()
      } catch (error) {
        this.entryFeedback = error.message
      }
    },
    handleUploadFile(event) {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }
      if (!file.name.toLowerCase().endsWith('.txt')) {
        this.uploadFeedback = '仅支持 txt 文件'
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        this.uploadPayload.message = reader.result || ''
        if (!this.uploadPayload.subject) {
          this.uploadPayload.subject = file.name.replace(/\.txt$/i, '')
        }
        this.uploadFeedback = ''
      }
      reader.onerror = () => {
        this.uploadFeedback = '文件读取失败'
      }
      reader.readAsText(file, 'UTF-8')
    },
    async submitUpload() {
      if (!this.uploadPayload.message.trim()) {
        this.uploadFeedback = '请先填写或上传 TXT 文本'
        return
      }
      if (!this.uploadPayload.subject.trim()) {
        this.uploadFeedback = '请补充文件名'
        return
      }
      const authPassword = this.authPassword
      if (!authPassword) {
        this.uploadFeedback = '请先验证管理口令'
        return
      }
      this.uploadPending = true
      this.uploadFeedback = ''
      try {
        const response = await fetch(buildApiPath('/api/admin.php?action=upload'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...this.uploadPayload,
            password: authPassword,
          }),
        })
        if (!response.ok) {
          throw new Error('上传失败')
        }
        const payload = await response.json()
        if (!payload.success) {
          throw new Error(payload.message || '上传失败')
        }
        this.uploadFeedback = '上传成功'
        this.uploadPayload.message = ''
        this.uploadPayload.subject = ''
        await this.loadEntries()
      } catch (error) {
        this.uploadFeedback = error.message
      } finally {
        this.uploadPending = false
      }
    },
  },
}
</script>

<style scoped>
.admin-shell {
  padding: 1.5rem 2rem 2rem;
  min-height: 100vh;
  background: #0b0f1a;
  color: #f8fafc;
}

.admin-header h1 {
  margin: 0.3rem 0;
  font-size: 2rem;
}

.admin-tag {
  font-size: 0.8rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #94a3b8;
}

.admin-auth {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-width: 420px;
}

.admin-auth input {
  padding: 0.7rem 0.85rem;
  border-radius: 12px;
  border: 1px solid #1f2937;
  background: #0f172a;
  color: #f8fafc;
}

.admin-auth button {
  padding: 0.8rem 1rem;
  border-radius: 12px;
  border: none;
  background: #6366f1;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.admin-body {
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.admin-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 1rem;
}

.entry-list,
.entry-editor {
  background: #111827;
  border-radius: 18px;
  padding: 1.2rem;
  border: 1px solid #1f2937;
  min-height: 420px;
}

.entry-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.9rem;
}

.entry-list-header h2 {
  margin: 0;
  font-size: 1.1rem;
}

.entry-row {
  margin-bottom: 0.9rem;
  padding: 0.8rem;
  border-radius: 14px;
  border: 1px solid transparent;
  transition: border-color 0.2s ease, background 0.2s ease;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: center;
}

.entry-row.deleted {
  opacity: 0.6;
}

.entry-row.active {
  border-color: #6366f1;
}

.entry-main {
  flex: 1;
  cursor: pointer;
}

.entry-subject {
  margin: 0;
  font-weight: 600;
}

.entry-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.entry-actions {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.entry-actions button {
  padding: 0.45rem 0.8rem;
  border-radius: 10px;
  border: 1px solid #374151;
  background: transparent;
  color: #f8fafc;
  cursor: pointer;
  font-size: 0.85rem;
  transition: border-color 0.2s ease;
}

.entry-actions button:hover {
  border-color: #6366f1;
}

.entry-editor article header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.entry-editor h2 {
  margin: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.8rem;
}

.form-row {
  display: flex;
  gap: 0.75rem;
}

.form-row .form-group {
  flex: 1;
}

.form-group input,
.form-group textarea {
  border-radius: 12px;
  border: 1px solid #1f2937;
  background: #0f172a;
  padding: 0.65rem 0.75rem;
  color: #f8fafc;
  font-size: 0.95rem;
}

.form-group textarea {
  resize: vertical;
}

.editor-actions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.editor-actions button {
  border: none;
  border-radius: 12px;
  padding: 0.7rem 1rem;
  background: #22c55e;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.upload-card {
  background: #111827;
  border-radius: 18px;
  border: 1px solid #1f2937;
  padding: 1.2rem;
}

.upload-card h2 {
  margin-top: 0;
}

.hint {
  font-size: 0.9rem;
  color: #94a3b8;
}

.inline-error {
  color: #f87171;
  margin: 0;
}

.empty-state {
  color: #9ca3af;
  font-size: 0.95rem;
}

@media (max-width: 1100px) {
  .admin-grid {
    grid-template-columns: 1fr;
  }
}
</style>
